import React, { useEffect, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Global settings for all social ad templates:
 *   - overrides: { [templateKey]: { field: value, ... } }
 *   - archived:  [templateKey, ...]
 *
 * All social ad pages/components share this hook so text edits and archive
 * state stay in sync with MongoDB (single source of truth).
 */
export const useAdSettings = () => {
  const [overrides, setOverrides] = useState({});
  const [archived, setArchived] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/social-ads/settings`);
      if (!r.ok) return;
      const data = await r.json();
      setOverrides(data.overrides || {});
      setArchived(data.archived || []);
      setLoaded(true);
    } catch (e) {
      console.warn('Failed to load ad settings', e);
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveOverrides = useCallback(async (templateKey, patch) => {
    const merged = { ...(overrides[templateKey] || {}), ...patch };
    setOverrides(prev => ({ ...prev, [templateKey]: merged }));
    await fetch(`${API_URL}/api/social-ads/overrides/${templateKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(merged),
    });
  }, [overrides]);

  const archive = useCallback(async (templateKey) => {
    setArchived(prev => prev.includes(templateKey) ? prev : [...prev, templateKey]);
    await fetch(`${API_URL}/api/social-ads/archive/${templateKey}`, { method: 'POST' });
  }, []);

  const unarchive = useCallback(async (templateKey) => {
    setArchived(prev => prev.filter(k => k !== templateKey));
    await fetch(`${API_URL}/api/social-ads/unarchive/${templateKey}`, { method: 'POST' });
  }, []);

  return { overrides, archived, loaded, refresh, saveOverrides, archive, unarchive };
};
